import { AST } from '../ast';
import { Expression } from '../builder/expression';
import { And, Case, Constant, If, NullIf, Or } from '../builder/utilities';
import { Ty } from '../ty';

export const categorize = ({
  logic,
}: AST._Categorize<AST.ExprMacroChildren>): Expression => {
  if (logic.length === 0) {
    return Constant(Ty.NOT_APPLICABLE);
  }

  // Create direct mapping of conditions to outcomes without nesting
  const branches: Array<{ when: Expression; then: Expression | string }> = [];

  // Process each rule and add a clean condition -> result mapping
  logic.forEach((rule) => {
    const expr = Expression.fromAst(rule.expr);

    const filterExpr = rule.filter ? Expression.fromAst(rule.filter) : null;

    //TODO
    //maybe check all conditions and if any are "when true" then move that branch to the top

    branches.push({
      when: filterExpr ? filterExpr.isNull().or(expr.isNull()) : expr.isNull(),
      then: Constant(null, { ty: 'string' }),
    });

    // Handle boolean and non-boolean expressions differently
    if (expr.implements('boolean')) {
      branches.push({
        when: filterExpr ? filterExpr.and(expr) : expr,
        then: rule.category,
      });
    } else {
      // For non-boolean expressions
      const exprValid = expr.neq(Ty.NOT_APPLICABLE);

      branches.push({
        when: filterExpr ? filterExpr.and(exprValid) : exprValid,
        then: Constant(rule.category).concat(Constant('~>')).concat(expr),
      });
    }
  });

  return Case(branches, {
    else: Ty.NOT_APPLICABLE,
  });
};

export const categoryAt = (
  target: string | Expression | null,
  at: number | Expression | null,
  opts?: { jsStackPointer?: Function }
): Expression => {
  const t = Expression.wrap(target, { ty: 'string' });
  // 1-indexed so we add 1 to allow for 0-indexin
  const a = Expression.wrap(at, { ty: 'int' }).add(1);

  return If(And(t.isNotNull(opts), a.isNotNull(opts)), {
    then: NullIf(t.splitPart(Ty.ENUM_PATH_SEPERATOR, a, opts), '').coalesce(
      Ty.NOT_APPLICABLE,
      opts
    ),
  });
};

export const categoryConcat = (
  parent: null | string | Expression,
  child: null | string | Expression
): Expression => {
  // Handle direct null values at compile time
  if (parent === null || child === null) {
    return Constant(null, { ty: 'string' });
  }

  const p = Expression.wrap(parent, { ty: 'string' });
  const c = Expression.wrap(child, { ty: 'string' });

  // Create optimized CASE statement with minimal conditions
  return Case(
    [
      // Case 1: Runtime null check - only need to check once, not separately
      {
        when: Or(p.isNull(), c.isNull()),
        then: Constant(null, { ty: 'string' }),
      },

      // Case 2: When child is N/A, return parent directly
      {
        when: c.eq(Ty.NOT_APPLICABLE),
        then: p,
      },

      // Case 3: When parent is N/A, return N/A
      {
        when: p.eq(Ty.NOT_APPLICABLE),
        then: Ty.NOT_APPLICABLE,
      },
    ],
    {
      else: p.concat(Constant(Ty.ENUM_PATH_SEPERATOR)).concat(c),
    }
  );
};
